import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Receipt } from "lucide-react";

const CATEGORIES = [
  { value: "productos", label: "Productos / químicos" },
  { value: "agua_luz", label: "Agua y luz" },
  { value: "alquiler", label: "Alquiler" },
  { value: "sueldos", label: "Sueldos" },
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "marketing", label: "Marketing" },
  { value: "general", label: "Otros / general" },
];

export default function NewExpense() {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!description.trim() || !(n > 0)) {
      toast.error("Indica descripción e importe válido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        description: description.trim(),
        amount: n,
        category,
      });
      toast.success("Gasto registrado");
      setDescription("");
      setAmount("");
      setCategory("general");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="new-expense-page">
      <header>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Nuevo gasto
        </h1>
        <p className="text-slate-500 mt-1">
          Registra un gasto del negocio.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 max-w-2xl space-y-5"
      >
        <div className="flex items-center gap-2 text-slate-700">
          <div className="w-9 h-9 rounded-md bg-red-50 text-red-600 border border-red-100 flex items-center justify-center">
            <Receipt className="w-4 h-4" />
          </div>
          <span className="font-display font-semibold">
            Datos del gasto
          </span>
        </div>

        <div>
          <Label className="mb-1.5 block">Descripción *</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ej: Compra de jabón industrial"
            required
            data-testid="expense-description-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Importe (€) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              data-testid="expense-amount-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">Categoría</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="expense-category-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6"
          data-testid="expense-submit-button"
        >
          {submitting ? "Guardando…" : "Registrar gasto"}
        </Button>
      </form>
    </div>
  );
}
